// What a submitted game *is*, built from the log and nothing else.
//
// **Pure, and that is what makes the disclosure checkable.** The consent
// dialog makes a promise about what leaves the machine; if the payload were
// assembled inside a fetch call, the only way to audit that promise would be
// to watch the network tab. Here it is one function over a command log, so a
// test can assert the exact set of keys that goes out — and
// `submission.test.ts` does, which is the test that fails when somebody adds a
// field the screen does not mention.
//
// **The log is the whole game, and the screen says so.** A command log replays
// to exactly one sequence of states (PLAN §5.5), so this is not a summary of
// what somebody did — it is a recording of it, complete enough to watch back.
// That is precisely why it is worth collecting and precisely why it may not be
// collected quietly. The derived metrics ride along because the server would
// otherwise replay every log to answer "how long was it", and a replay needs
// the rules the log was produced under.

import type { Command } from "src/engine/rules/commands";

import { mechanicsOfLog, MECHANICS_VERSION, type MechanicCounts } from "./mechanics";
import { type Scorecard, scoreLog } from "./scorecard";

/** Bumped when the shape below changes, so a reader can tell them apart. */
export const SUBMISSION_VERSION = 1;

/**
 * How the game was played, which is the one thing the log cannot say.
 *
 * A run and a versus game are the same command vocabulary — `state.run` being
 * present is what separates them, and that is state rather than a command —
 * so a reader replaying the log would have to reconstruct it. Named here
 * instead.
 */
export type GameKind = "run" | "scenario" | "versus" | "local" | "watch";

export interface Submission {
  v: number;
  /** The random per-browser id from `consent.ts`. Never derived from anything. */
  client: string;
  /** The disclosure version the player agreed to. */
  consent: number;
  kind: GameKind;
  /** Which build produced it, so a log can be replayed against the right rules. */
  build: string;
  /** When the client sent it, ms since epoch. */
  at: number;
  /** The game, entire. */
  log: readonly Command[];
  metrics: {
    scorecard: Scorecard;
    mechanics: MechanicCounts;
    mechanicsVersion: number;
  };
}

export interface SubmissionInput {
  client: string;
  kind: GameKind;
  build: string;
  log: readonly Command[];
  /** Injected so the builder stays pure and a test can pin the field. */
  at: number;
  consent: number;
}

/**
 * Build the submission.
 *
 * **Everything here is derived from the log or named by the caller** — there is
 * no reach into `window`, no cookie read, no seat name, no room id. A room id
 * would be the obvious thing to include and is deliberately absent: it is the
 * shared secret in the invite link, so it identifies the *other player* as
 * surely as it identifies this one, and they did not answer the question.
 *
 * The metrics are computed here rather than on the server because replaying a
 * log needs the rules that produced it, and the server is the one place that
 * might be a deploy ahead of the client that played it.
 */
export function buildSubmission(input: SubmissionInput): Submission {
  return {
    v: SUBMISSION_VERSION,
    client: input.client,
    consent: input.consent,
    kind: input.kind,
    build: input.build,
    at: input.at,
    log: input.log,
    metrics: {
      scorecard: scoreLog(input.log),
      mechanics: mechanicsOfLog(input.log),
      mechanicsVersion: MECHANICS_VERSION
    }
  };
}

/**
 * Roughly how big this will be on the wire.
 *
 * The client checks before sending so an over-long game is dropped with a
 * console line rather than rejected by the server as a 413 nobody sees. A
 * cheap `JSON.stringify` because the alternative is estimating, and a wrong
 * estimate here means the honest failure is replaced by a silent one.
 */
export const submissionBytes = (submission: Submission): number =>
  JSON.stringify(submission).length;
